import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reqdocsApi, ReqRule, ReqRuleDraft, ReqDoc, ReqAppDoc, ReqDateKind, ReqPreview } from '../../utils/api/reqdocs';

// Admin → Jobs → Required documents: the catalogue the Complete gate reads (server: services/jobRequiredDocRules.ts).
// Each rule picks services by a name pattern and lists the documents a job of that service must file before it can be
// marked Complete. Matching runs on the server only — the tester below asks the server, so what it shows is what the
// gate will do.

const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
});
const field: React.CSSProperties = {
  padding: '6px 8px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', minWidth: 0,
};
const mono: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const iconBtn: React.CSSProperties = { border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', padding: '2px 4px' };
const lbl: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 3, display: 'block' };
const hint: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 3, lineHeight: 1.4 };

type Company = 'auto' | 'never' | 'required';
const companyOf = (d: ReqDoc): Company => (d.needsCompany ? 'required' : d.noCompany ? 'never' : 'auto');
const blankDoc = (): ReqDoc => ({ key: '', docName: '', dateKind: 'expiry', required: true });
// A new application document has no key yet: the server makes one from its name on save.
const blankAppDoc = (): ReqAppDoc => ({ key: '', docName: '', count: 1 });
const blankRule = (): ReqRuleDraft => ({ label: '', match: '', not: '', expand: '', docs: [blankDoc()], notes: '', active: true, appDocs: [] });
const draftOf = (r: ReqRule): ReqRuleDraft => ({
  id: r.id, label: r.label, match: r.match, not: r.not, expand: r.expand, notes: r.notes, active: r.active,
  docs: r.docs.map(d => ({ ...d })), appDocs: (r.appDocs || []).map(a => ({ ...a })),
});
// What the server stores: no read-only counts, no empty optional fields.
const clean = (d: ReqRuleDraft): ReqRuleDraft => ({
  ...d,
  docs: d.docs.map(({ filed, ...x }) => {
    const o: ReqDoc = { key: x.key.trim(), docName: x.docName.trim(), dateKind: x.dateKind, required: !!x.required };
    if (x.noEmail) o.noEmail = true;
    if (x.folderHint && x.folderHint.trim()) o.folderHint = x.folderHint.trim();
    if (x.noCompany) o.noCompany = true;
    if (x.needsCompany) o.needsCompany = true;
    if (x.noTaxId) o.noTaxId = true;
    if (x.when && x.when.trim()) o.when = x.when.trim();
    return o;
  }),
  appDocs: (d.appDocs || []).map(a => ({ key: a.key, docName: a.docName.trim(), count: Number(a.count) })),
});

export default function JobRequiredDocs() {
  const { t } = useTranslation();
  const [rules, setRules] = useState<ReqRule[] | null>(null);
  const [dateKinds, setDateKinds] = useState<ReqDateKind[]>(['expiry', 'issued', 'signed', 'sent', 'confirmed', 'reviewed', 'submitted']);
  const [maxExp, setMaxExp] = useState(4);
  const [appMax, setAppMax] = useState({ docs: 30, count: 12 });
  const [editing, setEditing] = useState<{ id: string | null; draft: ReqRuleDraft; dirty: boolean } | null>(null);
  const [draftTest, setDraftTest] = useState<ReqPreview | null>(null);
  const [testName, setTestName] = useState('');
  const [test, setTest] = useState<ReqPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const timer = useRef<any>(null);

  const load = () => reqdocsApi.list()
    .then(d => {
      setRules(d.rules || []); if (d.dateKinds?.length) setDateKinds(d.dateKinds); if (d.maxExpansions) setMaxExp(d.maxExpansions);
      if (d.maxAppDocs && d.maxAppCount) setAppMax({ docs: d.maxAppDocs, count: d.maxAppCount });
    })
    .catch((e: any) => setErr(e.message));
  useEffect(() => { load(); }, []);   // eslint-disable-line

  // The tester asks the server, so it shows exactly what the gate will do with the SAVED rules.
  const runTest = (name: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (!name.trim()) { setTest(null); return; }
    timer.current = setTimeout(() => { reqdocsApi.preview(name).then(setTest).catch((e: any) => setErr(e.message)); }, 300);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => { runTest(testName); }, [rules]);   // eslint-disable-line

  const labelOf = (id: string) => { const r = (rules || []).find(x => x.id === id); return r ? (r.label || r.match) : id; };
  const dk = (k: string) => t(`rqd_dk_${k}`);

  const open = (r: ReqRule | null) => {
    if (editing?.dirty && !window.confirm(t('rqd_discard'))) return;
    setErr(''); setDone(false); setDraftTest(null);
    setEditing(r ? { id: r.id, draft: draftOf(r), dirty: false } : { id: null, draft: blankRule(), dirty: false });
  };
  const close = () => { if (editing?.dirty && !window.confirm(t('rqd_discard'))) return; setEditing(null); setDraftTest(null); };
  const patch = (p: Partial<ReqRuleDraft>) => setEditing(e => (e ? { ...e, draft: { ...e.draft, ...p }, dirty: true } : e));
  const patchDoc = (i: number, p: Partial<ReqDoc>) => setEditing(e => (e ? { ...e, dirty: true, draft: { ...e.draft, docs: e.draft.docs.map((d, j) => (j === i ? { ...d, ...p } : d)) } } : e));
  const moveDoc = (i: number, by: number) => setEditing(e => {
    if (!e) return e;
    const a = [...e.draft.docs]; const [x] = a.splice(i, 1); a.splice(i + by, 0, x);
    return { ...e, dirty: true, draft: { ...e.draft, docs: a } };
  });
  const patchApp = (i: number, p: Partial<ReqAppDoc>) => setEditing(e => (e ? { ...e, dirty: true, draft: { ...e.draft, appDocs: e.draft.appDocs.map((a, j) => (j === i ? { ...a, ...p } : a)) } } : e));
  const moveApp = (i: number, by: number) => setEditing(e => {
    if (!e) return e;
    const a = [...e.draft.appDocs]; const [x] = a.splice(i, 1); a.splice(i + by, 0, x);
    return { ...e, dirty: true, draft: { ...e.draft, appDocs: a } };
  });

  // A save or delete that would drop a key filed documents use comes back 409 — ask, then send it again with force.
  const withForce = async <T,>(call: (force: boolean) => Promise<T>): Promise<T | null> => {
    try { return await call(false); }
    catch (e: any) {
      if (e?.status === 409 && e?.payload?.code === 'key_in_use' && window.confirm(t('rqd_force_confirm', { msg: e.message }))) return call(true);
      throw e;
    }
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true); setErr(''); setDone(false);
    try {
      const body = clean(editing.draft);
      const out = await withForce(force => (editing.id ? reqdocsApi.update(editing.id, body, force) : reqdocsApi.create(body)));
      if (out) { setEditing(null); setDraftTest(null); setDone(true); await load(); }
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const remove = async (r: ReqRule) => {
    if (!window.confirm(t('rqd_delete_confirm'))) return;
    setBusy(true); setErr('');
    try { const out = await withForce(force => reqdocsApi.remove(r.id, force)); if (out) { if (editing?.id === r.id) setEditing(null); await load(); } }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const move = async (i: number, by: number) => {
    if (!rules) return;
    const ids = rules.map(r => r.id); const [x] = ids.splice(i, 1); ids.splice(i + by, 0, x);
    setBusy(true); setErr('');
    try { const out = await reqdocsApi.reorder(ids); setRules(out.rules); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const testDraft = async () => {
    if (!editing) return;
    if (!testName.trim()) { setErr(t('rqd_need_test_name')); return; }
    setErr('');
    try { setDraftTest(await reqdocsApi.preview(testName, clean(editing.draft))); }
    catch (e: any) { setErr(e.message); setDraftTest(null); }
  };

  const docsList = (docs: ReqDoc[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {docs.map(d => (
        <div key={d.key} style={{ fontSize: 12.5, color: 'var(--text-primary)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <span>{d.docName}</span>
          <span style={{ ...mono, fontSize: 11.5, color: 'var(--text-tertiary)' }}>{d.key}</span>
          <span style={{ fontSize: 11.5, color: d.required ? '#B45309' : 'var(--text-secondary)' }}>{d.required ? t('rqd_required') : t('rqd_optional')}</span>
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{dk(d.dateKind)}</span>
          {d.noEmail && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}><i className="ti ti-mail-off" aria-hidden /> {t('rqd_no_email')}</span>}
        </div>
      ))}
    </div>
  );
  const previewBox = (p: ReqPreview | null) => p && (
    <div style={{ marginTop: 8, padding: '8px 11px', borderRadius: 8, background: 'var(--c-gray-50)', border: '1px solid var(--border)' }}>
      {p.rules.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('rqd_test_none')}</div>
      ) : (
        <>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('rqd_test_rules')}: {p.rules.map(labelOf).join(' · ')}</div>
          {docsList(p.docs)}
        </>
      )}
    </div>
  );

  const editor = (e: { id: string | null; draft: ReqRuleDraft }) => {
    const d = e.draft;
    return (
      <div style={{ border: '1px solid var(--accent)', borderRadius: 10, padding: 12, margin: '8px 0', display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--bg-surface)' }}>
        <div>
          <label style={lbl}>{t('rqd_label')}</label>
          <input value={d.label} onChange={ev => patch({ label: ev.target.value })} maxLength={200} placeholder={t('rqd_label_ph')} style={{ ...field, width: '100%' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
          <div>
            <label style={lbl}>{t('rqd_match')}</label>
            <input value={d.match} onChange={ev => patch({ match: ev.target.value })} style={{ ...field, ...mono, width: '100%' }} />
            <div style={hint}>{t('rqd_match_hint')}</div>
          </div>
          <div>
            <label style={lbl}>{t('rqd_not')}</label>
            <input value={d.not} onChange={ev => patch({ not: ev.target.value })} style={{ ...field, ...mono, width: '100%' }} />
          </div>
        </div>
        <div>
          <label style={lbl}>{t('rqd_expand')}</label>
          <input value={d.expand} onChange={ev => patch({ expand: ev.target.value })} style={{ ...field, ...mono, width: '100%' }} />
          <div style={hint}>{t('rqd_expand_hint', { max: maxExp })}</div>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ ...lbl, marginBottom: 0, flex: 1 }}>{t('rqd_docs')}</span>
            <button onClick={() => patch({ docs: [...d.docs, blankDoc()] })} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>
              <i className="ti ti-plus" aria-hidden /> {t('rqd_add_doc')}
            </button>
          </div>
          <div style={hint}>{t('rqd_key_hint')}</div>
          {d.docs.map((doc, i) => (
            <div key={i} style={{ borderTop: '1px solid var(--border)', padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={doc.key} onChange={ev => patchDoc(i, { key: ev.target.value })} placeholder={t('rqd_key')} title={t('rqd_key_hint')}
                  style={{ ...field, ...mono, width: 150 }} />
                <input value={doc.docName} onChange={ev => patchDoc(i, { docName: ev.target.value })} placeholder={t('rqd_doc_name')}
                  style={{ ...field, flex: '1 1 220px' }} />
                <select value={doc.dateKind} onChange={ev => patchDoc(i, { dateKind: ev.target.value as ReqDateKind })} title={t('rqd_date_kind')} style={field}>
                  {dateKinds.map(k => <option key={k} value={k}>{dk(k)}</option>)}
                </select>
                <select value={doc.required ? 'req' : 'opt'} onChange={ev => patchDoc(i, { required: ev.target.value === 'req' })} style={field}>
                  <option value="req">{t('rqd_required')}</option>
                  <option value="opt">{t('rqd_optional')}</option>
                </select>
                {!!doc.filed && <span style={{ fontSize: 11.5, color: '#B45309' }} title={t('rqd_key_hint')}>{t('rqd_filed', { n: doc.filed })}</span>}
                <div style={{ flex: 1 }} />
                <button onClick={() => moveDoc(i, -1)} disabled={i === 0} title={t('rqd_up')} style={{ ...iconBtn, opacity: i === 0 ? 0.35 : 1 }}><i className="ti ti-arrow-up" aria-hidden /></button>
                <button onClick={() => moveDoc(i, 1)} disabled={i === d.docs.length - 1} title={t('rqd_down')} style={{ ...iconBtn, opacity: i === d.docs.length - 1 ? 0.35 : 1 }}><i className="ti ti-arrow-down" aria-hidden /></button>
                <button onClick={() => patch({ docs: d.docs.filter((_, j) => j !== i) })} title={t('rqd_remove_doc')} style={{ ...iconBtn, color: '#E24B4A' }}><i className="ti ti-trash" aria-hidden /></button>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={doc.folderHint || ''} onChange={ev => patchDoc(i, { folderHint: ev.target.value })} placeholder={t('rqd_folder')} title={t('rqd_folder_hint')}
                  style={{ ...field, width: 170 }} />
                <label title={t('rqd_company')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)' }}>
                  {t('rqd_company')}
                  <select value={companyOf(doc)} onChange={ev => { const v = ev.target.value as Company; patchDoc(i, { noCompany: v === 'never', needsCompany: v === 'required' }); }} style={field}>
                    <option value="auto">{t('rqd_company_auto')}</option>
                    <option value="never">{t('rqd_company_never')}</option>
                    <option value="required">{t('rqd_company_required')}</option>
                  </select>
                </label>
                {/* The tax ID is its own box, not a third option of the company one: a document may keep the
                    company and drop the tax ID (the 90 Day Report, report 316730e8). */}
                <label title={t('rqd_no_tax_id_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!doc.noTaxId} onChange={ev => patchDoc(i, { noTaxId: ev.target.checked })} /> {t('rqd_no_tax_id')}
                </label>
                <input value={doc.when || ''} onChange={ev => patchDoc(i, { when: ev.target.value })} placeholder={t('rqd_when')} title={t('rqd_when_hint')}
                  style={{ ...field, ...mono, width: 170 }} />
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!doc.noEmail} onChange={ev => patchDoc(i, { noEmail: ev.target.checked })} /> {t('rqd_no_email')}
                </label>
              </div>
            </div>
          ))}
        </div>

        {/* Report 54fd6fc9: what goes INTO the application, and how many of each, beside what the job files when it is
            done (above). Saved with the rule. */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ ...lbl, marginBottom: 0, flex: 1 }}>{t('rqd_app_docs')}</span>
            <button onClick={() => patch({ appDocs: [...d.appDocs, blankAppDoc()] })} disabled={d.appDocs.length >= appMax.docs}
              style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), opacity: d.appDocs.length >= appMax.docs ? 0.5 : 1 }}>
              <i className="ti ti-plus" aria-hidden /> {t('rqd_add_doc')}
            </button>
          </div>
          <div style={hint}>{t('rqd_app_docs_hint', { docs: appMax.docs, each: appMax.count })}</div>
          {d.appDocs.length === 0 && <div style={{ ...hint, color: 'var(--text-secondary)', padding: '6px 0' }}>{t('rqd_app_none')}</div>}
          {d.appDocs.map((a, i) => (
            <div key={i} style={{ borderTop: '1px solid var(--border)', padding: '6px 0', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', minWidth: 20, textAlign: 'right' }}>{i + 1}.</span>
              <input value={a.docName} onChange={ev => patchApp(i, { docName: ev.target.value })} placeholder={t('rqd_doc_name')} maxLength={200}
                style={{ ...field, flex: '1 1 260px' }} />
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)' }}>
                {t('rqd_app_count')}
                <input type="number" min={1} max={appMax.count} step={1} value={a.count || ''} onChange={ev => patchApp(i, { count: Number(ev.target.value) })}
                  style={{ ...field, width: 64 }} />
              </label>
              <div style={{ flex: 1 }} />
              <button onClick={() => moveApp(i, -1)} disabled={i === 0} title={t('rqd_up')} style={{ ...iconBtn, opacity: i === 0 ? 0.35 : 1 }}><i className="ti ti-arrow-up" aria-hidden /></button>
              <button onClick={() => moveApp(i, 1)} disabled={i === d.appDocs.length - 1} title={t('rqd_down')} style={{ ...iconBtn, opacity: i === d.appDocs.length - 1 ? 0.35 : 1 }}><i className="ti ti-arrow-down" aria-hidden /></button>
              <button onClick={() => patch({ appDocs: d.appDocs.filter((_, j) => j !== i) })} title={t('rqd_remove_doc')} style={{ ...iconBtn, color: '#E24B4A' }}><i className="ti ti-trash" aria-hidden /></button>
            </div>
          ))}
        </div>

        <div>
          <label style={lbl}>{t('rqd_notes')}</label>
          <textarea value={d.notes} onChange={ev => patch({ notes: ev.target.value })} rows={3} maxLength={4000} style={{ ...field, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        {previewBox(draftTest)}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-primary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={d.active} onChange={ev => patch({ active: ev.target.checked })} /> {t('rqd_active')}
          </label>
          <div style={{ flex: 1 }} />
          <button onClick={testDraft} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}><i className="ti ti-flask" aria-hidden /> {t('rqd_test_draft')}</button>
          <button onClick={close} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('rqd_cancel')}</button>
          <button onClick={save} disabled={busy} style={{ ...btn('var(--accent)'), opacity: busy ? 0.6 : 1 }}>{t('rqd_save')}</button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 820, margin: '16px auto 0' }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <i className="ti ti-file-check" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('rqd_title')}</div>
          <button onClick={() => open(null)} disabled={rules === null || busy} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>
            <i className="ti ti-plus" aria-hidden /> {t('rqd_add')}
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>{t('rqd_desc')}</div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>{t('rqd_test_label')}</label>
          <input value={testName} onChange={ev => { setTestName(ev.target.value); runTest(ev.target.value); }} placeholder={t('rqd_test_ph')} style={{ ...field, width: '100%' }} />
          {previewBox(test)}
        </div>

        {editing && editing.id === null && editor(editing)}

        {rules === null ? (
          !err && <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>
        ) : rules.length === 0 ? (
          <div style={{ fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 11px' }}>{t('rqd_empty')}</div>
        ) : rules.map((r, i) => (
          <div key={r.id} style={{ borderTop: '1px solid var(--border)', padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span title={r.active ? t('rqd_active') : t('rqd_inactive')} style={{ width: 8, height: 8, borderRadius: 4, background: r.active ? '#1E9E63' : 'var(--c-gray-300, #ccc)', flex: '0 0 auto' }} />
              <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                <div style={{ fontSize: 13, color: r.active ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{r.label || r.match}</div>
                <div style={{ ...mono, fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.match}>
                  {r.match}{r.not ? `  ∖  ${r.not}` : ''}
                </div>
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('rqd_docs_count', { n: r.docs.length })}</span>
              {!!r.appDocs?.length && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }} title={t('rqd_app_docs')}>{t('rqd_app_docs_count', { n: r.appDocs.length })}</span>}
              <button onClick={() => move(i, -1)} disabled={i === 0 || busy} title={t('rqd_up')} style={{ ...iconBtn, opacity: i === 0 ? 0.35 : 1 }}><i className="ti ti-arrow-up" aria-hidden /></button>
              <button onClick={() => move(i, 1)} disabled={i === rules.length - 1 || busy} title={t('rqd_down')} style={{ ...iconBtn, opacity: i === rules.length - 1 ? 0.35 : 1 }}><i className="ti ti-arrow-down" aria-hidden /></button>
              <button onClick={() => (editing?.id === r.id ? close() : open(r))} title={t('rqd_edit')} style={iconBtn}><i className="ti ti-pencil" aria-hidden /></button>
              <button onClick={() => remove(r)} disabled={busy} title={t('rqd_delete')} style={{ ...iconBtn, color: '#E24B4A' }}><i className="ti ti-trash" aria-hidden /></button>
            </div>
            {editing && editing.id === r.id && editor(editing)}
          </div>
        ))}

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 10, whiteSpace: 'pre-wrap' }}>{err}</div>}
        {done && !editing && <div style={{ fontSize: 12, color: '#15803D', marginTop: 10 }}>{t('rqd_saved')}</div>}
      </div>
    </div>
  );
}
